/*
 * Where data-action paths are resolved. "Person.List.ShowDetail" walks
 * registered area objects first, then any lazily-registered area (loaded on
 * first use), then -- for scripts not yet migrated -- the window globals.
 */

export type ActionHandler = (this: Element, ...args: unknown[]) => unknown;
export type AreaModule = Record<string, unknown>;

const areas = new Map<string, AreaModule>();
const lazy = new Map<string, () => Promise<AreaModule>>();
const loading = new Map<string, Promise<AreaModule>>();

/** Register an area's handler tree under its top-level name ("Person"). */
export function registerArea(name: string, api: AreaModule): void {
    const existing = areas.get(name);
    areas.set(name, existing ? { ...existing, ...api } : api);
}

/** Register an area whose module is imported the first time one of its actions fires. */
export function registerLazyArea(name: string, loader: () => Promise<AreaModule>): void {
    lazy.set(name, loader);
}

/** Registered (loaded) area object, or undefined. */
export function area(name: string): AreaModule | undefined {
    return areas.get(name);
}

function walk(root: unknown, parts: string[]): unknown {
    let current: unknown = root;
    for (const part of parts) {
        if (current === null || current === undefined) return undefined;
        current = (current as Record<string, unknown>)[part];
    }
    return current;
}

function asHandler(value: unknown): ActionHandler | null {
    return typeof value === 'function' ? (value as ActionHandler) : null;
}

/** Resolve without loading anything: registry, then window globals. */
export function resolveActionSync(path: string | null | undefined): ActionHandler | null {
    if (!path) return null;
    const parts = String(path).split('.');
    const head = parts[0] ?? '';
    const registered = areas.get(head);
    if (registered) {
        const found = asHandler(walk(registered, parts.slice(1)));
        if (found) return found;
    }
    return asHandler(walk(window, parts));
}

/** True when the path's area is known only as a lazy loader (not yet imported). */
export function needsLoad(path: string): boolean {
    const head = path.split('.')[0] ?? '';
    return !areas.has(head) && lazy.has(head);
}

/** Resolve, importing a lazy area on first use. */
export async function resolveAction(path: string | null | undefined): Promise<ActionHandler | null> {
    const direct = resolveActionSync(path);
    if (direct || !path) return direct;
    const head = path.split('.')[0] ?? '';
    const loader = lazy.get(head);
    if (!loader) return null;
    let pending = loading.get(head);
    if (!pending) {
        pending = loader().then(api => { registerArea(head, api); lazy.delete(head); loading.delete(head); return api; });
        loading.set(head, pending);
    }
    await pending;
    return resolveActionSync(path);
}

/** Test support. */
export function clearRegistry(): void {
    areas.clear();
    lazy.clear();
    loading.clear();
}
